import React, { useEffect, useState, useRef, lazy } from 'react';
import { ShoppingBag } from 'lucide-react';
import { scanProducts } from '../lib/productScanner';
import { BUYER_NAMES, US_CITIES, ScannedProduct, FALLBACK_PRODUCTS } from '../lib/purchaseData';
interface PurchaseEvent {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
}
const FIRST_DELAY_MS = 3000;
const MIN_INTERVAL_MS = 8000;
const MAX_INTERVAL_MS = 15000;
const VISIBLE_MS = 4000;
function randomInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function pickWeighted(products: ScannedProduct[]): ScannedProduct {
  // If any products have weights, use weighted random.
  const weighted = products.filter(p => typeof p.weight === 'number');
  if (weighted.length === products.length && products.length > 0) {
    const total = products.reduce((s, p) => s + (p.weight || 0), 0);
    let r = Math.random() * total;
    for (const p of products) {
      r -= p.weight || 0;
      if (r <= 0) return p;
    }
    return products[products.length - 1];
  }
  return pickRandom(products);
}
function formatPrice(p: ScannedProduct): string {
  if (p.priceText) return p.priceText;
  return `$${p.priceNumber} USD`;
}
export function RecentPurchaseToast() {
  const [event, setEvent] = useState<PurchaseEvent | null>(null);
  const [phase, setPhase] = useState<'enter' | 'visible' | 'leave'>('enter');
  const recentNamesRef = useRef<string[]>([]);
  const productsRef = useRef<ScannedProduct[]>([]);
  const queueRef = useRef<PurchaseEvent[]>([]);
  const timersRef = useRef<number[]>([]);
  const idRef = useRef(0);
  const reducedMotionRef = useRef(false);
  // Scan products once on mount (after slight delay so DOM is settled)
  useEffect(() => {
    const t = window.setTimeout(() => {
      try {
        productsRef.current = scanProducts();
      } catch {
        productsRef.current = FALLBACK_PRODUCTS;
      }
    }, 200);
    // Check reduced motion preference
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotionRef.current = mq.matches;
    const onChange = () => {
      reducedMotionRef.current = mq.matches;
    };
    mq.addEventListener?.('change', onChange);
    return () => {
      window.clearTimeout(t);
      mq.removeEventListener?.('change', onChange);
    };
  }, []);
  // Generate and queue purchase events
  useEffect(() => {
    const generate = (): PurchaseEvent => {
      // Pick a name not in last 10 if possible
      const recent = recentNamesRef.current;
      let pool = BUYER_NAMES.filter(n => !recent.includes(n));
      if (pool.length === 0) pool = BUYER_NAMES;
      const name = pickRandom(pool);
      recentNamesRef.current = [...recent, name].slice(-10);
      const city = pickRandom(US_CITIES);
      const products = productsRef.current.length > 0 ? productsRef.current : FALLBACK_PRODUCTS;
      const product = pickWeighted(products);
      idRef.current += 1;
      return {
        id: idRef.current,
        name,
        city,
        product
      };
    };
    const showNext = () => {
      // Queue an event if nothing is showing
      if (event !== null) {
        queueRef.current.push(generate());
        return;
      }
      const next = queueRef.current.shift() ?? generate();
      setPhase(reducedMotionRef.current ? 'visible' : 'enter');
      setEvent(next);
      // After a frame, switch to visible (slide in finishes)
      const t1 = window.setTimeout(() => {
        setPhase('visible');
      }, 50);
      timersRef.current.push(t1);
      // After VISIBLE_MS, start leave animation
      const t2 = window.setTimeout(() => {
        setPhase('leave');
      }, VISIBLE_MS);
      timersRef.current.push(t2);
      // After leave animation, clear and schedule next
      const t3 = window.setTimeout(() => {
        setEvent(null);
        // Schedule the next toast at a random interval
        const nextDelay = randomInt(MIN_INTERVAL_MS, MAX_INTERVAL_MS);
        const t4 = window.setTimeout(showNext, nextDelay);
        timersRef.current.push(t4);
      }, VISIBLE_MS + (reducedMotionRef.current ? 0 : 500));
      timersRef.current.push(t3);
    };
    const initial = window.setTimeout(showNext, FIRST_DELAY_MS);
    timersRef.current.push(initial);
    return () => {
      timersRef.current.forEach(id => window.clearTimeout(id));
      timersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!event) return null;
  const reduced = reducedMotionRef.current;
  const baseClasses = 'fixed left-1/2 -translate-x-1/2 bottom-4 sm:bottom-6 z-[60] max-w-[92vw] sm:max-w-md w-auto';
  const motionClasses = reduced ? '' : phase === 'enter' ? 'opacity-0 translate-y-6' : phase === 'leave' ? 'opacity-0 translate-y-6' : 'opacity-100 translate-y-0';
  const transitionClass = reduced ? '' : 'transition-all duration-500 ease-out';
  // Force consistent pricing for this single-product landing — always show
  // the actual offer price ($37) with the original ($197) strikethrough,
  // regardless of what the page scanner picks up.
  const product = {
    ...event.product,
    priceText: '$37 USD',
    originalPriceText: '$197 USD'
  };
  const productLabel = 'Más Años Juntos';
  return <div className={`${baseClasses} ${transitionClass} ${motionClasses}`} style={{
    transform: 'translateX(-50%)' + (reduced ? '' : '')
  }} role="status" aria-live="polite" data-id="element-205">
      <div className="flex items-center gap-3 bg-white rounded-2xl shadow-2xl border border-sage-light/40 px-3 py-2.5 pr-4" data-id="element-206">
        {product.thumbnailUrl ? <img src={product.thumbnailUrl} alt="" width={48} height={48} loading="lazy" decoding="async" className="w-12 h-12 rounded-xl object-cover shrink-0 border border-sage-light/30" data-id="element-207" /> : <div className="w-12 h-12 rounded-xl bg-sage-light/40 flex items-center justify-center shrink-0" data-id="element-208">
            <ShoppingBag className="w-5 h-5 text-sage-dark" data-id="element-209" />
          </div>}
        <div className="min-w-0" data-id="element-210">
          <p className="text-sm text-gray-800 leading-snug" data-id="element-211">
            <span className="font-bold" data-id="element-212">{event.name}</span>
            <span className="text-gray-500" data-id="element-213"> en </span>
            <span className="font-medium" data-id="element-214">{event.city}</span>
          </p>
          <p className="text-xs text-gray-600 leading-snug" data-id="element-215">
            acaba de comprar{' '}
            <span className="font-semibold text-gray-800" data-id="element-216">{productLabel}</span>
            {' — '}
            <span className="font-bold text-terracotta" data-id="element-217">
              {formatPrice(product)}
            </span>
            {product.originalPriceText && <span className="ml-1.5 text-gray-400 line-through decoration-red-400" data-id="element-218">
                {product.originalPriceText}
              </span>}
          </p>
        </div>
      </div>
    </div>;
}